import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';

import { cn } from './cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, type = 'text', ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      aria-invalid={invalid || undefined}
      className={cn(
        'flex h-9 w-full rounded-auditor border border-line-strong bg-surface px-3 py-2 text-sm text-ink',
        'placeholder:text-ink-subtle focus-visible:border-brand focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand/20',
        'disabled:cursor-not-allowed disabled:bg-canvas disabled:opacity-60',
        'aria-[invalid=true]:border-critical aria-[invalid=true]:ring-critical/20',
        className,
      )}
      {...props}
    />
  ),
);

Input.displayName = 'Input';

export interface AdornedInputProps extends InputProps {
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  /** Classes for the bordered wrapper; `className` goes to the input itself. */
  wrapperClassName?: string;
}

/** An input with content inside its border, such as a unit, an icon or a small button. */
export const AdornedInput = forwardRef<HTMLInputElement, AdornedInputProps>(
  ({ className, disabled, endAdornment, invalid, startAdornment, type = 'text', wrapperClassName, ...props }, ref) => (
    <div
      className={cn(
        'flex h-9 w-full items-center gap-1.5 rounded-auditor border border-line-strong bg-surface px-2.5 text-sm text-ink',
        'focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20',
        invalid && 'border-critical ring-critical/20',
        disabled && 'cursor-not-allowed bg-canvas opacity-60',
        wrapperClassName,
      )}
    >
      {startAdornment != null && <span className="flex shrink-0 items-center text-ink-muted">{startAdornment}</span>}
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-ink outline-hidden placeholder:text-ink-subtle',
          'disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      />
      {endAdornment != null && <span className="flex shrink-0 items-center text-ink-muted">{endAdornment}</span>}
    </div>
  ),
);

AdornedInput.displayName = 'AdornedInput';
